import { useEffect, useState } from "react";
import api from "@/lib/api";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarCheck, Printer, IndianRupee, ReceiptText, Percent, Tag, RotateCcw, BookUser } from "lucide-react";

const fmtRs = (v) => `₹${Number(v || 0).toFixed(2)}`;

export default function Closing() {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/reports/closing", { params: { date } }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  return (
    <div className="space-y-5" data-testid="closing-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <CalendarCheck className="w-6 h-6 text-emerald-700" /> Daily Closing (Z-Report)
          </h1>
          <p className="text-sm text-slate-500">Day-end summary — for cash drawer matching.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input data-testid="closing-date" type="date" value={date} max={format(new Date(), "yyyy-MM-dd")} onChange={(e) => setDate(e.target.value)} className="h-11 w-44" />
          <Button data-testid="closing-print-btn" variant="outline" onClick={() => window.print()} className="h-11"><Printer className="w-4 h-4 mr-2" /> Print</Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <div className="print-area space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi tid="cl-revenue" icon={IndianRupee} label="Total Sale" value={fmtRs(data.revenue)} />
            <Kpi tid="cl-bills" icon={ReceiptText} label="Bills" value={data.bills} />
            <Kpi tid="cl-gst" icon={Percent} label="GST" value={fmtRs(data.gst_total)} />
            <Kpi tid="cl-discount" icon={Tag} label="Discount" value={fmtRs(data.discount_total)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-6 border-slate-200">
              <h3 className="font-display font-bold">Payment Split (Cash Match)</h3>
              <div className="mt-4 space-y-2.5">
                {data.payment_split.map((p) => (
                  <div key={p.mode} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm font-medium uppercase text-slate-700">{p.mode} <span className="text-xs text-slate-400">({p.count})</span></span>
                    <span className="font-mono-num font-bold text-emerald-900">{fmtRs(p.amount)}</span>
                  </div>
                ))}
                {data.payment_split.length === 0 && <p className="text-sm text-slate-400">No sales on this day.</p>}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-200 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500 flex items-center gap-1.5"><BookUser className="w-3.5 h-3.5" /> Credit given (due)</span><span className="font-mono-num text-red-600">{fmtRs(data.credit_due_added)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Returns ({data.returns_count})</span><span className="font-mono-num text-red-600">- {fmtRs(data.returns_total)}</span></div>
                <div className="flex justify-between font-bold pt-1 border-t border-slate-100"><span>Net Cash Expected</span><span className="font-mono-num text-emerald-900" data-testid="cl-net-cash">{fmtRs((data.payment_split.find((p) => p.mode === "cash")?.amount || 0) - data.returns_total)}</span></div>
              </div>
            </Card>

            <Card className="p-6 border-slate-200">
              <h3 className="font-display font-bold">Bills of the Day ({data.bills})</h3>
              <div className="mt-3 max-h-72 overflow-y-auto space-y-1.5">
                {data.invoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50 last:border-0">
                    <div>
                      <span className="font-mono-num font-semibold">{inv.invoice_no}</span>
                      <span className="text-xs text-slate-500 ml-2">{inv.customer_name}</span>
                    </div>
                    <span className="font-mono-num">{fmtRs(inv.grand_total)}</span>
                  </div>
                ))}
                {data.invoices.length === 0 && <p className="text-sm text-slate-400">No bills.</p>}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tid }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="font-mono-num text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-700" />
        </div>
      </div>
    </Card>
  );
}
